import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { PiggyBank } from 'lucide-react';
import type { TFunction } from 'i18next';
import MobilePageHeader from '../../../shared/mobile/MobilePageHeader';
import { ResponsiveTable, MobileCardField } from '../../../shared/mobile/ResponsiveTable';
import RestaurantSelector from '../../../common/RestaurantSelector';
import { ReportLoading } from '../../reports/hub/common';
import { useAnalysisStore } from '../../../../stores/appAnalysisStore';
import { MoneySaverGroup, MoneySaverMember } from '../../../../services/analysis/analysisService';
import { eur } from '../../../orderhistory/utils';
import { formatCategoryName } from '../../../orderhistory/sections/FilterControls';
import { cn } from '../../../../lib/utils';
import {
  borderGrey,
  cardClass,
  greenTextColor,
  primaryText,
  redTextColor,
  secondaryText,
  semiBoldText,
  tertiaryText,
} from '../../../../styling/stylingHelper';
import {
  AnalysisDetailHeader,
  AnalysisSearch,
  RankedGroupRow,
  SAVINGS_BADGE_CLASS,
  matchesFoodDrinks,
} from '../shared/AnalysisControls';
import { useAnalysisSelection } from '../shared/useAnalysisSelection';
import { fmtShopQty, unitLabel } from '../shared/analysisUnits';

const PAGE_SIZE = 30;

const priceLabel = (t: TFunction, m: MoneySaverMember) =>
  m.comparable && m.unitPrice !== null && m.unit !== null ? `${eur(m.unitPrice)}/${unitLabel(t, m.unit)}` : t('analysis.moneySavers.notComparable');

/** Analysis → Money savers / product swapping opportunities, last 60 days (spec 3). Super admin only in v1. */
const MoneySaversPage: React.FC = () => {
  const { isSuperAdmin } = useAnalysisSelection();
  if (!isSuperAdmin) return <Navigate to="/analysis" replace />;
  return <MoneySaversPageBody />;
};

const MoneySaversPageBody: React.FC = () => {
  const { t } = useTranslation();
  const { restaurantSelection, isAllRestaurants } = useAnalysisSelection();
  const filter = useAnalysisStore(s => s.foodDrinksFilter);
  const data = useAnalysisStore(s => s.moneySaversBySelection[restaurantSelection]);
  const loading = useAnalysisStore(s => !!s.moneySaversLoading[restaurantSelection]);
  const error = useAnalysisStore(s => s.moneySaversError[restaurantSelection]);
  const loadMoneySavers = useAnalysisStore(s => s.loadMoneySavers);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    if (restaurantSelection) loadMoneySavers(restaurantSelection);
  }, [restaurantSelection, loadMoneySavers]);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.groups ?? [])
      .filter(g => matchesFoodDrinks(filter, g.side))
      .filter(g => !q || g.name.toLowerCase().includes(q) ||
        g.members.some(m => m.name.toLowerCase().includes(q) || m.supplier.toLowerCase().includes(q)));
  }, [data, filter, search]);

  const total = useMemo(() => groups.reduce((s, g) => s + g.savings, 0), [groups]);

  const toggleExpanded = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div>
      <MobilePageHeader title={t('analysis.moneySavers.title')} right={<RestaurantSelector />} />
      <div className="space-y-4">
        <AnalysisDetailHeader
          icon={<PiggyBank className={cn('w-5 h-5', greenTextColor)} />}
          title={t('analysis.moneySavers.title')}
          subtitle={t('analysis.moneySavers.formula')}
          totalLabel={t('analysis.moneySavers.totalLabel')}
          totalValue={eur(total)}
          totalClassName={greenTextColor}
        />
        <AnalysisSearch value={search} onChange={setSearch} placeholder={t('analysis.searchPlaceholder')} />

        {loading && !data ? (
          <ReportLoading />
        ) : error && !data ? (
          <div className={cn(cardClass, 'p-8 text-center text-sm', redTextColor)}>{t('analysis.loadError')}</div>
        ) : groups.length === 0 ? (
          <div className={cn(cardClass, 'p-8 text-center text-sm', secondaryText)}>{t('analysis.moneySavers.empty')}</div>
        ) : (
          <div className="space-y-2">
            {groups.slice(0, visibleCount).map((g, i) => (
              <MoneySaverGroupRow
                key={g.id}
                rank={i + 1}
                group={g}
                isAllRestaurants={isAllRestaurants}
                expanded={expanded.has(g.id)}
                onToggleExpanded={() => toggleExpanded(g.id)}
              />
            ))}
            {groups.length > visibleCount && (
              <button
                type="button"
                onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                className={cn('w-full py-2 text-sm rounded-lg border', borderGrey, secondaryText)}
              >
                {t('analysis.loadMore')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface MoneySaverGroupRowProps {
  rank: number;
  group: MoneySaverGroup;
  isAllRestaurants: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
}

const MoneySaverGroupRow: React.FC<MoneySaverGroupRowProps> = ({ rank, group, isAllRestaurants, expanded, onToggleExpanded }) => {
  const { t } = useTranslation();
  const meta = [
    group.category ? formatCategoryName(group.category) : '',
    t('analysis.productsCount', { count: group.members.length }),
    group.qty !== null && group.unit !== null ? t('analysis.moneySavers.bought', { qty: fmtShopQty(t, group.qty, group.unit) }) : '',
    t('analysis.moneySavers.spent', { amount: eur(group.spend) }),
  ].filter(Boolean).join(' · ');

  const productCell = (m: MoneySaverMember) => (
    <>
      <p className={cn('text-sm', semiBoldText, primaryText)}>{m.name}</p>
      <p className={cn('text-xs', secondaryText)}>{m.supplier}</p>
      {m.isCheapest && m.cheapestBoughtBy && (isAllRestaurants || !(m.qty && m.qty > 0)) && (
        <p className={cn('text-xs', tertiaryText)}>{t('analysis.moneySavers.boughtBy', { restaurant: m.cheapestBoughtBy })}</p>
      )}
      {isAllRestaurants && m.byRestaurant.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {m.byRestaurant.map(b => (
            <p key={b.restaurantId} className={cn('text-xs', tertiaryText)}>
              {b.restaurantName}: {fmtShopQty(t, b.qty, m.unit)} · {eur(b.spend)}
            </p>
          ))}
        </div>
      )}
    </>
  );

  const priceCell = (m: MoneySaverMember) => (
    <span className="inline-flex items-center gap-1.5 justify-end flex-wrap">
      <span className={m.comparable ? primaryText : tertiaryText}>{priceLabel(t, m)}</span>
      {m.isCheapest && (
        <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-bold uppercase', SAVINGS_BADGE_CLASS)}>{t('analysis.moneySavers.cheapest')}</span>
      )}
    </span>
  );

  const savingsLabel = (n: number) => (n > 0 ? `+${eur(n)}` : '—');

  return (
    <RankedGroupRow
      rank={rank}
      title={group.name}
      meta={meta}
      badge={`+${eur(group.savings)}`}
      badgeClassName={SAVINGS_BADGE_CLASS}
      expanded={expanded}
      onToggleExpanded={onToggleExpanded}
    >
      <ResponsiveTable
        desktop={
          <table className="w-full text-sm mt-2">
            <thead>
              <tr className={cn('text-left text-xs', tertiaryText)}>
                <th className="py-2 font-medium">{t('analysis.col.product')}</th>
                <th className="py-2 font-medium text-right">{t('analysis.col.price')}</th>
                <th className="py-2 font-medium text-right">{t('analysis.col.bought')}</th>
                <th className="py-2 font-medium text-right">{t('analysis.col.spent')}</th>
                <th className="py-2 font-medium text-right">{t('analysis.col.savings')}</th>
              </tr>
            </thead>
            <tbody>
              {group.members.map(m => (
                <tr key={m.key} className={cn('border-t', borderGrey)}>
                  <td className="py-2 pr-2">{productCell(m)}</td>
                  <td className="py-2 text-right">{priceCell(m)}</td>
                  <td className={cn('py-2 text-right', primaryText)}>{fmtShopQty(t, m.qty, m.unit)}</td>
                  <td className={cn('py-2 text-right', primaryText)}>{eur(m.spend)}</td>
                  <td className={cn('py-2 text-right', semiBoldText, greenTextColor)}>{savingsLabel(m.savings)}</td>
                </tr>
              ))}
              <tr className={cn('border-t', borderGrey)}>
                <td className={cn('py-2', semiBoldText, primaryText)} colSpan={2}>{t('analysis.total')}</td>
                <td className={cn('py-2 text-right', semiBoldText, primaryText)}>{fmtShopQty(t, group.qty, group.unit)}</td>
                <td className={cn('py-2 text-right', semiBoldText, primaryText)}>{eur(group.spend)}</td>
                <td className={cn('py-2 text-right', semiBoldText, greenTextColor)}>{savingsLabel(group.savings)}</td>
              </tr>
            </tbody>
          </table>
        }
        mobile={
          <div className="space-y-2 mt-2">
            {group.members.map(m => (
              <div key={m.key} className={cn('rounded-lg border p-3', borderGrey)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">{productCell(m)}</div>
                </div>
                <div className="mt-2">{priceCell(m)}</div>
                <div className="flex mt-2">
                  <MobileCardField label={t('analysis.col.bought')} value={fmtShopQty(t, m.qty, m.unit)} divider />
                  <MobileCardField label={t('analysis.col.spent')} value={eur(m.spend)} divider />
                  <MobileCardField label={t('analysis.col.savings')} value={savingsLabel(m.savings)} />
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between px-1">
              <span className={cn('text-sm', semiBoldText, primaryText)}>{t('analysis.total')}</span>
              <span className={cn('text-sm', semiBoldText, greenTextColor)}>{savingsLabel(group.savings)}</span>
            </div>
          </div>
        }
      />
    </RankedGroupRow>
  );
};

export default MoneySaversPage;
